import { createTRPCProxyClient, httpBatchLink } from "@trpc/client";
import { createAuthClient } from "better-auth/client";
import { emailOTPClient } from "better-auth/client/plugins";
import { useCallback, useEffect, useRef } from "react";
import type { AppRouter } from "@/backend/api/router";
import { UI_TIMEZONE } from "@/configConstants";
import type { AttendanceStatus, EventRecord } from "@/types/event-attendance";
import { parseEventDate } from "./utils/dates";
// ============================================================================
// TRPC & Auth Clients
// ============================================================================

export function createFrontendTrpcClient() {
	return createTRPCProxyClient<AppRouter>({
		links: [
			httpBatchLink({
				url: "/api/trpc",
				fetch: (input, init) =>
					fetch(input, { ...init, credentials: "include" }),
			}),
		],
	});
}

export function createFrontendAuthClient() {
	return createAuthClient({
		basePath: "/api/auth",
		plugins: [emailOTPClient()],
	});
}

// ============================================================================
// Date & Time Formatting
// ============================================================================

function deDate(dateStr: string, timeStr: string): Date {
	// Build a Date in Europe/Berlin for display/formatting purposes
	const [hh = "00", mm = "00"] = (timeStr ?? "").split(":");
	const dt = parseEventDate(dateStr).set({
		hour: Number(hh),
		minute: Number(mm),
	});
	return dt.toJSDate();
}

const FMT = {
	weekday: new Intl.DateTimeFormat("de-DE", {
		weekday: "long",
		timeZone: UI_TIMEZONE,
	}),
	date: new Intl.DateTimeFormat("de-DE", {
		day: "2-digit",
		month: "2-digit",
		timeZone: UI_TIMEZONE,
	}),
	time: new Intl.DateTimeFormat("de-DE", {
		hour: "2-digit",
		minute: "2-digit",
		timeZone: UI_TIMEZONE,
	}),
};

export function formatDeDateRange(
	date: string,
	startTime: string,
	endTime: string,
): { weekday: string; date: string; timeRange: string } {
	const start = deDate(date, startTime);
	const end = deDate(date, endTime);
	if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
		console.log("problem with date called with:", date, startTime, endTime);
		return { weekday: "", date: "", timeRange: "" };
	}
	const weekday = FMT.weekday.format(start);
	const datePart = FMT.date.format(start);
	const startTimePart = FMT.time.format(start);
	const endTimePart = FMT.time.format(end);
	return {
		weekday,
		date: datePart,
		timeRange: `${startTimePart} - ${endTimePart}`,
	};
}

// ============================================================================
// Currency Formatting
// ============================================================================

export function formatCurrency(cents: number): string {
	return (cents / 100).toLocaleString("de-DE", {
		style: "currency",
		currency: "EUR",
	});
}

// ============================================================================
// Local Storage for Attendance
// ============================================================================

export type LocalAttendance = Record<number, AttendanceStatus>; // eventId -> status

export function readLocalAttendance(
	storageKey = "attendance_draft",
): LocalAttendance {
	try {
		const raw = localStorage.getItem(storageKey);
		return raw ? (JSON.parse(raw) as LocalAttendance) : {};
	} catch {
		return {};
	}
}

export function writeLocalAttendance(
	map: LocalAttendance,
	storageKey = "attendance_draft",
) {
	try {
		localStorage.setItem(storageKey, JSON.stringify(map));
	} catch {
		// ignore
	}
}

// ============================================================================
// Event Grouping
// ============================================================================

export function groupByMonth(events: EventRecord[]): [string, EventRecord[]][] {
	const bucket: Record<string, EventRecord[]> = {};
	for (const ev of events) {
		const d = parseEventDate(ev.date);
		const key = `${d.year}-${String(d.month).padStart(2, "0")}`;
		if (!bucket[key]) bucket[key] = [];
		bucket[key].push(ev);
	}
	return Object.entries(bucket).sort(([a], [b]) => a.localeCompare(b));
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Debounce hook keyed per id (e.g., user id)
 * Allows multiple independent debounced actions
 */
export function usePerKeyDebounce(delay = 500) {
	const timers = useRef<Record<string, number>>({});
	const schedule = useCallback(
		(id: string, fn: () => void) => {
			const prev = timers.current[id];
			if (prev) clearTimeout(prev);
			timers.current[id] = window.setTimeout(fn, delay);
		},
		[delay],
	);
	useEffect(
		() => () => Object.values(timers.current).forEach(clearTimeout),
		[],
	);
	return { schedule };
}

// ============================================================================
// Form Validation
// ============================================================================

/**
 * Validates an email address
 * @returns Error message if invalid, null if valid
 */
export function validateEmail(value: string): string | null {
	const trimmed = value.trim();
	if (!trimmed) return "Bitte E-Mail eingeben";
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	if (!emailRegex.test(trimmed)) return "Bitte eine gültige E-Mail angeben";
	return null;
}

/**
 * Validates a profile field (displayname, firstname, lastname, nickname)
 * @param value - The field value to validate
 * @param fieldName - The name of the field (for error messages)
 * @param minLength - Minimum length (default: 3)
 * @returns Error message if invalid, null if valid
 */
export function validateProfileField(
	value: string,
	fieldName: string,
	minLength = 3,
): string | null {
	const trimmed = value.trim();
	if (!trimmed) return `${fieldName} ist erforderlich`;
	if (trimmed.length < minLength) {
		return `${fieldName} muss mindestens ${minLength} Zeichen lang sein`;
	}
	return null;
}
